/**
 * Notification message resolver — translates structured backend notifications
 * into the user's current language via i18next.
 *
 * Backend sends:
 *   { messageKey: "notifications.wallet.rejected", messageParams: { sym, amount, currency, notes } }
 *
 * This file resolves that into a human-readable string in the active language.
 * To support a new notification type, just add the key to all 3 locale files — no code changes needed.
 */
import i18n from '../i18n';

/**
 * Resolve a notification object to a display string in the user's current language.
 * Falls back to `notification.message` for old-format or unrecognized notifications.
 *
 * @param {object} notification - Notification object from backend
 * @returns {string} Translated message string
 */
export const resolveNotificationMessage = (notification) => {
  if (!notification) return '';

  if (notification.messageKey) {
    const params = notification.messageParams || {};

    // For package notifications: LO locale uses stationNameLA, others use stationName.
    // Inject both so the locale file can pick whichever it needs.
    const enrichedParams = {
      ...params,
      stationName: params.stationName || params.stationNameLA || '',
      stationNameLA: params.stationNameLA || params.stationName || '',
    };

    const translated = i18n.t(notification.messageKey, {
      ...enrichedParams,
      defaultValue: null,
    });
    // i18next returns the key itself when translation is missing — fall back to plain message
    if (translated && translated !== notification.messageKey) {
      return translated;
    }
  }

  return notification.message || '';
};

/**
 * Resolve station name in the user's current language.
 * Uses nameLA for Lao, falls back to name for other languages.
 */
export const resolveStationName = (station) => {
  if (!station) return '';
  const lang = i18n.language;
  if (lang === 'lo' && station.nameLA) return station.nameLA;
  return station.name || station.nameLA || '';
};
